import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { pToQ, qToAccuracy, qToChar, qToP } from "../lib/phred";

const inputCls =
  "border-black/15 bg-white text-neutral-900 placeholder:text-neutral-500 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900 dark:placeholder:text-neutral-500";

export function PhredCalc() {
  const locale = getLocale();
  const [q, setQ] = useState("20");
  const [p, setP] = useState("");

  const qOut = useMemo(() => {
    const n = Number(q);
    if (q.trim() === "" || !Number.isFinite(n)) return null;
    return {
      p: qToP(n),
      accuracy: qToAccuracy(n),
      c33: qToChar(n, 33),
      c64: qToChar(n, 64),
    };
  }, [q]);

  const pOut = useMemo(() => {
    const n = Number(p);
    if (p.trim() === "" || !Number.isFinite(n)) return null;
    return pToQ(n);
  }, [p]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <div className="space-y-1.5">
        <Label htmlFor="phred-q" className="text-muted-foreground">
          {m.phred_field_q()}
        </Label>
        <Input
          id="phred-q"
          type="number"
          inputMode="decimal"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={inputCls}
        />
      </div>

      {qOut ? (
        <>
          <div className="sci-result-zone mt-5 flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.phred_accuracy()}
            </span>
            <span
              data-testid="phred-accuracy"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {qOut.accuracy.toLocaleString(locale, {
                maximumFractionDigits: 4,
              })}
              %
            </span>
          </div>
          <SciStatGrid
            items={[
              {
                label: m.phred_error_prob(),
                value: qOut.p.toExponential(2),
              },
              { label: m.phred_char33(), value: qOut.c33 || "—" },
              { label: m.phred_char64(), value: qOut.c64 || "—" },
            ]}
          />
        </>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">{m.phred_hint_q()}</p>
      )}

      <hr className="my-6 border-[var(--border-c)]" />

      <div className="space-y-1.5">
        <Label htmlFor="phred-p" className="text-muted-foreground">
          {m.phred_field_p()}
        </Label>
        <Input
          id="phred-p"
          type="number"
          inputMode="decimal"
          placeholder="0.001"
          value={p}
          onChange={(e) => setP(e.target.value)}
          className={inputCls}
        />
      </div>
      <p className="mt-3 text-sm text-muted-foreground" aria-live="polite">
        {p.trim() === ""
          ? m.phred_hint_p()
          : pOut == null
            ? m.phred_hint_p_invalid()
            : m.phred_p_result({
                q: pOut.toLocaleString(locale, { maximumFractionDigits: 2 }),
              })}
      </p>
    </div>
  );
}
